"use client";

import { useEffect, useState } from "react";

// Self-healing boundary for cross-deploy client errors. After a rebuild, an open
// or soft-cached tab can (a) request JS/CSS chunks whose content-hash filenames
// no longer exist, or (b) POST a Server Action ID from a different build. Next
// surfaces (a) as a ChunkLoadError; for (b) the real "Failed to find Server
// Action" message is server-side only — in PRODUCTION the client boundary just
// receives the generic "An unexpected response was received from the server."
// We match all of these and reload so the tab pulls the current build.
//
// Auto-reload is capped (2 attempts per 60s window): a stale-tab mismatch heals
// on the first reload, while a genuinely persistent error stops reloading and
// shows the manual button instead of looping.
function isRecoverableDeployError(error?: {
	name?: string;
	message?: string;
}): boolean {
	if (!error) {
		return false;
	}
	const name = error.name || "";
	const message = error.message || "";
	return (
		name === "ChunkLoadError" ||
		/Loading chunk [\w-]+ failed/i.test(message) ||
		/Failed to load chunk|Loading CSS chunk [\w-]+ failed|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i.test(
			message,
		) ||
		// Cross-deploy Server Action mismatch: the dev-only message, the shared
		// "older/newer deployment" hint, and the generic message Next throws for it
		// in production.
		/Failed to find Server Action|from an older or newer deployment|An unexpected response was received from the server/i.test(
			message,
		)
	);
}

const RELOAD_KEY = "rb_deploy_reload";
const RELOAD_WINDOW_MS = 60000;
const MAX_AUTO_RELOADS = 2;

// Returns true if it triggered a reload (so the UI can render a quiet
// "Reloading…" instead of flashing the error), false if auto-recovery is
// exhausted and the manual screen should show.
function tryAutoReload(): boolean {
	try {
		const now = Date.now();
		let state: { n: number; at: number } = { n: 0, at: 0 };
		try {
			const parsed = JSON.parse(
				window.sessionStorage.getItem(RELOAD_KEY) || "{}",
			);
			if (parsed && typeof parsed === "object") {
				state = { n: Number(parsed.n) || 0, at: Number(parsed.at) || 0 };
			}
		} catch {
			/* ignore malformed state */
		}
		// A gap since the last auto-reload means this is a fresh incident, not a loop.
		if (now - state.at > RELOAD_WINDOW_MS) {
			state = { n: 0, at: 0 };
		}
		if (state.n >= MAX_AUTO_RELOADS) {
			return false;
		}
		window.sessionStorage.setItem(
			RELOAD_KEY,
			JSON.stringify({ n: state.n + 1, at: now }),
		);
		window.location.reload();
		return true;
	} catch {
		window.location.reload();
		return true;
	}
}

export default function GlobalError({
	error,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	// Optimistically hide the error text for a recoverable error; the effect
	// decides whether to reload or fall back to the manual screen.
	const [recovering, setRecovering] = useState(() =>
		isRecoverableDeployError(error),
	);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (!isRecoverableDeployError(error)) {
			return;
		}
		if (!tryAutoReload()) {
			setRecovering(false);
		}
	}, [error]);

	if (recovering) {
		return (
			<html lang="en">
				<body
					style={{
						display: "flex",
						minHeight: "100vh",
						alignItems: "center",
						justifyContent: "center",
						fontFamily: "system-ui, -apple-system, sans-serif",
						background: "#ffffff",
						color: "#999999",
						fontSize: "0.9rem",
						margin: 0,
					}}
				>
					Reloading…
				</body>
			</html>
		);
	}

	return (
		<html lang="en">
			<body
				style={{
					display: "flex",
					minHeight: "100vh",
					alignItems: "center",
					justifyContent: "center",
					fontFamily: "system-ui, -apple-system, sans-serif",
					background: "#ffffff",
					color: "#111111",
					margin: 0,
				}}
			>
				<div style={{ textAlign: "center", padding: "2rem", maxWidth: 440 }}>
					<h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: 8 }}>
						Something went wrong
					</h2>
					<p style={{ color: "#666666", fontSize: "0.9rem", marginBottom: 20 }}>
						The page failed to load. This can happen right after an update —
						reloading usually fixes it.
					</p>
					<button
						type="button"
						onClick={() => window.location.reload()}
						style={{
							padding: "0.5rem 1.25rem",
							borderRadius: 8,
							border: "none",
							background: "#111111",
							color: "#ffffff",
							fontSize: "0.9rem",
							cursor: "pointer",
						}}
					>
						Reload
					</button>
				</div>
			</body>
		</html>
	);
}
